import { useMemo, useState } from 'react';
import { useApp } from '../store';
import { NOBODY, isComplete } from '../signatories';
import { can } from '../permissions';
import DevicePicker from './DevicePicker';
import DateField from './DateField';
import { useInvalidDates } from './useInvalidDates';
import Info from './Info';
import {
  backLine,
  buildLine,
  deviceText,
  knownDevices,
  lastRelease,
  releaseProblem,
  nextBuildNumber,
  nextVersions,
  notYetReleased,
  returnedByPull,
  whoNumbersIt,
  type Draft,
} from '../releases';
import { today } from '../sprints';
import type { Build, Issue } from '../types';

/**
 * RECORDING A RELEASE — Option B, which Vinnie chose on 15 August ("build B
 * then A").
 *
 * A RELEASE IS ITS OWN ACT, not something that happens because a fortnight
 * ended. That is the whole of the choice: a hot-fix on a Tuesday, two releases
 * in one week, and a board that does not use sprints at all are all ordinary
 * here. The sprint-closing dialog uses this same component with its list
 * narrowed to that sprint — which is Option A, built on top rather than instead.
 *
 * WHO TYPES THE VERSION IS THE ONE RULE. With the switch off, a person does,
 * and the three buttons beside the box are SUGGESTIONS that are never applied
 * on their behalf. With it on, the number comes from the tool that pushes to
 * this board, and this panel says so rather than offering a second opinion.
 */
export default function CutRelease({
  sprintId,
  sprintName,
  onDone,
  onCancel,
}: {
  /** Narrows the offered work to one sprint — Option A. Absent is Option B. */
  sprintId?: string;
  sprintName?: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const builds = useApp((s) => s.builds);
  const issues = useApp((s) => s.issues);
  const cutRelease = useApp((s) => s.cutRelease);
  const assistantVersions = useApp((s) => s.meta.assistantVersions ?? false);
  // Off unless somebody said otherwise — migration 0024. Off, none of the device
  // machinery below is rendered and nothing is written, so a hosted product's
  // releases carry no device field at all rather than an empty one.
  const tracksDevices = useApp((s) => s.meta.tracksDevices ?? false);
  // The same rank that may change the setting, so nobody is pointed at a screen
  // that will refuse them — see the note beside the hint below.
  const canSettings = can(useApp((s) => s.shownRole), 'editSettings');

  /* This one was never the hang — it ends in a boolean, and booleans compare by
     value. Changed regardless, so the rule is "no selector builds an array" with
     nothing to remember about which ones are safe. */
  const nobodySigns = useApp((s) => (s.meta.signatories ?? NOBODY).filter(isComplete).length === 0);
  const last = useMemo(() => lastRelease(builds), [builds]);
  const suggestions = nextVersions(last?.version);
  /*
   * OFFERED, NOT TICKED FOR THEM — except that everything starts ticked,
   * because the common case is "all of it went out" and a list of empty boxes
   * makes recording a release into data entry. Unticking is the judgement:
   * internal work nobody outside would recognise does not belong in a
   * changelog, and no rule here can tell which that is.
   */
  const offered = useMemo(() => {
    const all = notYetReleased(issues, builds);
    return sprintId ? all.filter((i) => (i.sprint ?? '') === sprintId) : all;
  }, [issues, builds, sprintId]);

  /*
   * Which of these are back because a release was taken back — see
   * returnedByPull. Shown, because a rule nobody can observe reads as broken.
   */
  const back = useMemo(() => returnedByPull(offered, builds), [offered, builds]);

  const [version, setVersion] = useState('');
  const [date, setDate] = useState(() => today());
  // Hold Record while the date box holds an entry it could not read.
  const badDates = useInvalidDates();
  const [plain, setPlain] = useState('');
  const [left, setLeft] = useState<Set<string>>(new Set());
  const [problem, setProblem] = useState('');
  const [busy, setBusy] = useState(false);
  /*
   * WHAT IT WENT ONTO — and nothing is ticked to begin with.
   *
   * The opposite choice from the item list above, and for the opposite reason.
   * Everything finished is ticked because "all of it went out" is nearly always
   * true and unticking is the judgement. Devices are the reverse: a release
   * reaches the phones somebody actually installed it on, one at a time, often
   * over the following days. Pre-ticking the four from last time would record a
   * claim nobody made — and a device list is read as a statement of fact about
   * the world, not as a summary.
   */
  const [devices, setDevices] = useState<string[]>([]);
  // The project's own history is the list. See knownDevices — a built-in menu of
  // generic names would offer "Android phone" to somebody whose devices are
  // called S25, S26, Pixel and Fold.
  const offeredDevices = useMemo(() => knownDevices(builds), [builds]);

  const chosen = offered.filter((i) => !left.has(i.ref));
  const toggle = (ref: string) =>
    setLeft((was) => {
      const next = new Set(was);
      if (next.has(ref)) next.delete(ref);
      else next.add(ref);
      return next;
    });

  /*
   * EVERY REFUSAL IS SHOWN WHILE TYPING, not after pressing.
   *
   * `releaseProblem` is the one rule for whether a draft can be recorded, and
   * asking it on every keystroke rather than only on submit is the difference
   * between a button that will not work and a button that works and then says
   * no. Vinnie hit the second: *"the button says 'Record 1.2' which was used in
   * the previous test and 1.2 already exists."* It offered to do something it
   * was always going to refuse.
   *
   * The same function still runs in the store, because a screen is not a place
   * to enforce anything — two people on one board can reach it at once.
   */
  const wrongNow = useMemo(
    () => releaseProblem({ version, date, items: [] }, builds),
    [version, date, builds]
  );

  const record = async () => {
    if (badDates.ref.current) return;   // the date box holds something unreadable
    setBusy(true);
    const draft: Draft = {
      version,
      date,
      items: chosen.map((i) => i.ref),
      plain,
      ...(sprintId ? { sprint: sprintId } : {}),
      // Absent on a board that does not track devices, rather than empty. The
      // difference is the whole of the fix: empty means "nobody wrote it down",
      // absent means the question does not apply here.
      ...(tracksDevices ? { installed: deviceText(devices) } : {}),
    };
    /*
     * THE BUSY FLAG IS CLEARED WHATEVER HAPPENS. It was cleared only on the
     * happy path, so a refused write left the button reading "Recording…" for
     * ever with nothing said — which is precisely how this arrived as a bug
     * report rather than as an error message.
     */
    let wrong = '';
    try {
      wrong = await cutRelease(draft);
    } catch (e) {
      wrong =
        (e as { message?: string })?.message ||
        'That release could not be saved. Nothing was recorded — try again.';
    } finally {
      setBusy(false);
    }
    setProblem(wrong);
    if (!wrong) onDone();
  };

  return (
    <section className="panel cut">
      <div className="grp-heading">
        {sprintName
          ? `Record what ${sprintName} shipped`
          : /*
             * PREPARE, WHERE SOMEBODY SIGNS. Vinnie, 21 August: "cut a release"
             * sounds final, and being asked for approval afterwards reads
             * backwards. On a board that names nobody, nothing is waiting and
             * recording one still means it is out — so the old word is still
             * the true one there.
             */
            nobodySigns
            ? 'Cut a release'
            : 'Prepare a release'}
      </div>

      {/*
        * THE SWITCH, ANSWERED BEFORE ANYTHING IS ASKED. On a board whose
        * versions come from a tool, offering a version box first and explaining
        * afterwards is how somebody types a number that then disagrees with
        * their own software.
        */}
      {assistantVersions ? (
        <div className="cut-told" role="note">
          <p>{whoNumbersIt(true, last)}</p>
          <p className="hint">
            To record one here instead, turn off <strong>Versions come from the assistant</strong> in
            Settings → Project. It is a deliberate press rather than a field left open, so nobody
            overrides the tool by accident.
          </p>
          <div className="grp-foot">
            <button className="btn" onClick={onCancel}>
              Close
            </button>
          </div>
        </div>
      ) : (
        <>
          {/*
            * THREE CARDS, WHICH IS OPTION C — Vinnie's pick on 15 August after
            * *"it looks a bit busy and there is a lot of text… it is
            * overwhelming."*
            *
            * WHAT THE CARDS DO IS THE POINT. Six standing paragraphs are gone
            * and not one fact with them: each card answers one question — what
            * is this release called, what is in it, and what else is worth
            * saying — so the STRUCTURE carries what the prose was carrying.
            * The explanations moved behind the `i` dots this app already uses
            * on the release cards themselves, which is a pattern rather than a
            * new idea somebody has to learn.
            *
            * NOTHING FOLDS. Option B folded the optional half away and was
            * shorter still; it was not chosen, and the reason is worth keeping:
            * a folded row is easy to forget, and devices somebody meant to
            * record would quietly not be recorded.
            */}
          <div className="cut-card">
          <div className="cut-top">
            <label className="stack cut-version">
              Version
              <input
                className="input"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                placeholder="e.g. 1.5.0"
                autoFocus
              />
            </label>
            <div className="stack cut-date">
              <label className="stack-lbl" htmlFor="cut-date">Date it went out</label>
              <DateField id="cut-date" ariaLabel="Date it went out" value={date} onChange={setDate} onInvalidChange={badDates.mark('date')} variant="form" />
            </div>
            {suggestions && (
              <div className="stack cut-suggest">
                Suggestions
                <div className="cut-chips">
                  {([['patch', suggestions.patch], ['minor', suggestions.minor], ['major', suggestions.major]] as const).map(
                    ([kind, value]) => (
                      <button
                        key={kind}
                        type="button"
                        className={`btn btn-small ${version === value ? 'is-on' : ''}`}
                        onClick={() => setVersion(value)}
                        title={`A ${kind} release`}
                      >
                        {value}
                      </button>
                    )
                  )}
                </div>
              </div>
            )}
          </div>
            <p className="hint cut-last">{buildLine(last, nextBuildNumber(builds))}</p>
          </div>

          <div className="cut-card">
          <div className="grp-heading cut-what">
            {/* An element, not a bare text node, so the phone rule can give the
                title a line of its own. */}
            {/* Info WRAPS what it explains and puts the dot after it, so the
                title goes inside rather than the dot being dropped in beside
                it — that is what makes the label itself the thing you press. */}
            <Info
              className="cut-what-title"
              title="What belongs in a release"
              what={
                <>
                  <p>
                    Everything finished that no release has claimed yet
                    {sprintName ? `, narrowed to ${sprintName}` : ''}. Untick anything that did not
                    go out, or that nobody outside the team would recognise — a changelog is read by
                    people who do not work here.
                  </p>
                  {back.size > 0 && (
                    <p>
                      <strong>{backLine(back)}.</strong> That release was taken back, so the work it
                      carried has not reached anybody, whatever its changelog said. It is offered
                      again here, and each of those rows is tagged in the list.
                    </p>
                  )}
                </>
              }
            >
              What went out
            </Info>
            <span className="grp-sub">
              {chosen.length} of {offered.length}
              {sprintName ? ` in ${sprintName}` : ''}
              {back.size > 0 && <> · {backLine(back)}</>}
            </span>
            {/*
              * ONE PRESS TO CLEAR, because the first release on an established
              * board offers everything ever finished — 138 cards on
              * PhotoSyncApp — and unticking that by hand is not a decision
              * anybody makes, it is a chore they abandon halfway through.
              */}
            {offered.length > 1 && (
              <button
                type="button"
                className="btn btn-small cut-all"
                onClick={() =>
                  setLeft(chosen.length ? new Set(offered.map((i) => i.ref)) : new Set())
                }
              >
                {chosen.length ? 'Untick all' : 'Tick all'}
              </button>
            )}
          </div>
          <div className="grp-list is-contents cut-list">
            {offered.length === 0 ? (
              <p className="hint">
                {sprintName
                  ? `Nothing in ${sprintName} is finished and unreleased. You can still record the version — a rebuild that carries no changes is a real release.`
                  : 'Everything finished has already gone out. You can still record a version — a rebuild carrying no changes is a real release.'}
              </p>
            ) : (
              offered.map((i) => (
                <Row
                  key={i.id}
                  issue={i}
                  on={!left.has(i.ref)}
                  toggle={toggle}
                  back={back.get(i.ref)}
                />
              ))
            )}
          </div>
          </div>

          {/* THE QUIET CARD. Sunk rather than raised, and labelled once — which
              is the whole of what four sentences of "(optional — …)" were
              doing. A person can see at a glance that nothing below is asked
              of them, without reading a word of it. */}
          <div className="cut-card is-extra">
          <div className="grp-sub cut-extra-label">Optional</div>
          <label className="stack cut-plain">
            In one sentence
            <input
              className="input"
              value={plain}
              onChange={(e) => setPlain(e.target.value)}
              placeholder="e.g. Sign-in works end to end, and duplicates stop being copied."
            />
          </label>

          {/*
            * THE ONE PLACE THAT SAYS THE SETTING EXISTS. Everywhere else, off
            * means silent — that is the whole point of the switch, and a hosted
            * product must not be nagged about devices it does not have.
            *
            * But silent everywhere left no way in. Vinnie turned it on for one
            * board, went to look at the releases — which were on another board —
            * and found nothing: no picker, no buttons, and nothing on screen to
            * say why. The setting is per board, correctly (a workspace can hold
            * a phone app and a website), so the screen has to be able to say so.
            *
            * Here rather than on the cards, because this is the moment somebody
            * is actually recording what went out; and only to somebody who can
            * change it, because telling everybody else is nagging about a door
            * they cannot open.
            */}
          {!tracksDevices && canSettings && (
            <p className="hint cut-devices-off">
              Devices are not tracked on this project, so this release will not name any. Turn on{' '}
              <strong>This project ships to devices</strong> in Settings if it should — it is set
              per project.
            </p>
          )}

          {tracksDevices && (
            <div className="cut-devices">
              <div className="grp-heading cut-what">
                <Info
                  className="cut-what-title"
                  title="Naming what it reached"
                  what={
                    <>
                      <p>
                        Tap the ones it reached, or type a name that is not offered — whatever you
                        type is offered here next time, so a device only has to be named once.
                      </p>
                      <p>
                        Leave it empty if it has not been installed anywhere yet. A release usually
                        reaches its devices over the following days, and you can add them to a
                        recorded release at any time.
                      </p>
                    </>
                  }
                >
                  What it went onto
                </Info>
                {devices.length > 0 && <span className="grp-sub">{devices.length} chosen</span>}
              </div>
              <DevicePicker devices={devices} offered={offeredDevices} onChange={setDevices} />
            </div>
          )}
          </div>

          {problem && (
            <div className="login-error" role="alert">
              {problem}
            </div>
          )}

          <div className="grp-foot">
            <button
              className="btn btn-primary"
              disabled={busy || !!wrongNow || badDates.any}
              title={wrongNow || undefined}
              onClick={() => void record()}
            >
              {busy ? 'Recording…' : version.trim() ? `Record ${version.trim()}` : 'Record it'}
            </button>
            {wrongNow && <span className="hint cut-needs">{wrongNow}</span>}
            <button className="btn" onClick={onCancel}>
              Cancel
            </button>
          </div>
        </>
      )}
    </section>
  );
}

/**
 * One line of work, and whether it is being claimed.
 *
 * THE SPRINT IS SHOWN, and it is the detail that makes Option B honest: a
 * release may carry work from two fortnights and from none, and seeing that is
 * how somebody notices a hot-fix has been swept in with a sprint's work.
 */
function Row({
  issue,
  on,
  toggle,
  back,
}: {
  issue: Issue;
  on: boolean;
  toggle: (ref: string) => void;
  /** The release that carried this and was taken back, when there is one. */
  back?: Build;
}) {
  const sprints = useApp((s) => s.sprints);
  const where = issue.sprint ? sprints.find((s) => s.id === issue.sprint)?.name : undefined;
  return (
    <label className={`grp-row is-work cut-row ${on ? '' : 'is-left-out'}`}>
      <input type="checkbox" checked={on} onChange={() => toggle(issue.ref)} />
      <div className="work-main">
        <span className="grp-name">
          <span className="sprint-ref">{issue.ref}</span> {issue.title}
        </span>
      </div>
      <div className="work-meta">
        {/* Marked on the row as well as counted above, so it can be FOUND in a
            list of a hundred and thirty-eight rather than merely tallied. */}
        {back && (
          <span className="cut-back-tag" title={`Carried by ${back.version || `build ${back.build}`}, which was taken back`}>
            back from {back.version || `build ${back.build}`}
          </span>
        )}
        <span className="work-state">{where ?? 'No sprint'}</span>
      </div>
    </label>
  );
}
